import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, useIsMounted, useMediaQueries } from 'seagull';
import { routes } from '~/constants';
import { useIoContext, useStore } from '~/contexts';
import { useEventReporter } from '~/event-reporter';
import { useUtmsParams } from '~/hooks/useUtmsParams';
import { useLocaleKeys } from '~/i18n';
import { ServerError } from '~/infra/error-response/ErrorResponse';
import { handleRequest, sendErrReport } from '~/infra/utils';
import { LoginMethod, useLoginTelemetry } from '~/loginTelemetry/useLoginTelemetry';
import { errorMessages } from '~/pages/unAuthorizedPages/constants';
import { useStoreActions } from '~/store/store-context';
import { usePostAuthRedirect } from '~/utils/auth-redirect';
import styles from './GoogleButton.module.scss';
import { NativeGoogleButton } from './NativeGoogleButton';
const GOOGLE_AUTH_SCOPES = [
    'profile',
    'email'
];
export default function GoogleButton(props) {
    const { ensureEmail, testId, width, onAuthSuccess, onAuthError } = props;
    const { reportToGtm, reportToAppcues, reportToHubspot, reportToHeap } = useEventReporter();
    const { api, logBI } = useStoreActions();
    const { mobile } = useMediaQueries();
    const getUtms = useUtmsParams();
    const { GoogleButton, browser: { changeLocation, getOrigin }, cookies, native } = useIoContext();
    const useAuthorizationHeader = useStore((state)=>state.session.useAuthorizationHeader);
    const { pathname } = useLocation();
    const performPostAuthRedirect = usePostAuthRedirect();
    const { loginSucceeded, loginFailed } = useLoginTelemetry();
    const { t: bootRetryTexts } = useLocaleKeys('common.bootRetry');
    const resolvedWidth = width !== null && width !== void 0 ? width : mobile ? 280 : 400;
    const isAuthInFlight = useRef(false);
    const isMounted = useIsMounted();
    const [loading, setLoading] = useState(false);
    const buttonConfiguration = useMemo(()=>{
        return {
            type: 'standard',
            locale: 'en_US',
            width: resolvedWidth,
            text: 'continue_with',
            theme: 'outline',
            logo_alignment: 'center',
            size: 'large'
        };
    }, [
        resolvedWidth
    ]);
    function reportAuthFailure(label) {
        sendErrReport(label);
        loginFailed(LoginMethod.Google);
        onAuthError === null || onAuthError === void 0 ? void 0 : onAuthError();
    }
    function handleNativeAuthError() {
        loginFailed(LoginMethod.Google);
        onAuthError === null || onAuthError === void 0 ? void 0 : onAuthError();
    }
    async function onGoogleAuth(param) {
        let { credential } = param;
        if (isAuthInFlight.current) return;
        isAuthInFlight.current = true;
        setLoading(true);
        try {
            const authToken = await handleRequest(api.auth.oauthSignIn({
                provider: 'google',
                pageUrl: getOrigin() + pathname,
                idToken: credential,
                utms: getUtms(),
                ensureEmail
            }));
            if (!authToken.success) {
                if (authToken.error.status === 403 || authToken.error.body === 'invalid auth request: email does not match') {
                    loginFailed(LoginMethod.Google, 'email_does_not_match');
                    changeLocation("".concat(pathname).concat(routes.loginError));
                    return;
                }
                if (authToken.error.hasServerError(ServerError.EmailIsInvalid)) {
                    onAuthError && onAuthError(errorMessages.noBusinessEmailError);
                    loginFailed(LoginMethod.Google, 'invalid_email');
                } else if (authToken.error.hasServerError(ServerError.AccountIsDisabled)) {
                    loginFailed(LoginMethod.Google, 'account_disabled');
                    onAuthError === null || onAuthError === void 0 ? void 0 : onAuthError();
                } else if (authToken.error.isTransportFailure()) {
                    loginFailed(LoginMethod.Google, 'network_error');
                    onAuthError === null || onAuthError === void 0 ? void 0 : onAuthError(bootRetryTexts.body());
                } else {
                    const errorCode = authToken.error.serverErrorCode();
                    reportAuthFailure("GoogleButton - ".concat(errorCode !== null && errorCode !== void 0 ? errorCode : "status ".concat(authToken.error.status)));
                }
            } else if (authToken.data != null) {
                if (native.isNative && useAuthorizationHeader) {
                    cookies.setAuthTokenForNative(authToken.data);
                }
                reportToGtm('form-success', 'google-sign-in');
                reportToAppcues('form-success', 'google-sign-in');
                reportToHubspot('google_sign_in');
                reportToHeap('googleSignIn', 'form-success', 'google-sign-in');
                logBI(11801);
                onAuthSuccess && onAuthSuccess();
                loginSucceeded(LoginMethod.Google);
                performPostAuthRedirect();
            } else {
                sendErrReport('GoogleButton - did not retrieve an auth token');
                loginFailed(LoginMethod.Google);
                onAuthError && onAuthError();
            }
        } finally{
            isAuthInFlight.current = false;
            if (isMounted()) {
                setLoading(false);
            }
        }
    }
    const resolvedTestId = testId || 'google-login';
    return native.isNative ? /*#__PURE__*/ _jsx(NativeGoogleButton, {
        testId: resolvedTestId,
        width: resolvedWidth,
        scopes: GOOGLE_AUTH_SCOPES,
        onCredential: onGoogleAuth,
        onError: handleNativeAuthError
    }) : /*#__PURE__*/ _jsx(Box, {
        testId: resolvedTestId,
        className: styles.buttonContainer,
        children: /*#__PURE__*/ _jsx(GoogleButton, {
            googleClientId: window.googleAuthClientId,
            callback: onGoogleAuth,
            buttonConfiguration: buttonConfiguration,
            loading: loading,
            testId: resolvedTestId
        })
    });
}
